import { fg } from '@atlaskit/platform-feature-flags';
import type {
	MenuItemsAPIResponse,
	ProcessedSuggestionSection,
} from '@atlassian/navigation-bff-data-provider/universal-create';

import type {
	AppDefinedMenuSection,
	MenuSectionV2,
	RenderableMenuSectionData,
	SuggestionMenuSection,
} from '../../../../types/menuV2';

import { filterNativeItemsOnly, getHydrateMenuItems } from './getHydrateMenuItems';

/**
 * Type guard to check if a section is an app-defined section.
 */
export const isAppDefinedSection = (section: MenuSectionV2): section is AppDefinedMenuSection =>
	section.type === 'appDefinedSection';

/**
 * Type guard to check if a section is a suggestion section.
 */
export const isSuggestionSection = (section: MenuSectionV2): section is SuggestionMenuSection =>
	section.type === 'suggestionSection';

/**
 * Filters sections to get only app-defined sections.
 */
export const getAppDefinedSections = (sections: MenuSectionV2[]): AppDefinedMenuSection[] =>
	sections.filter(isAppDefinedSection);

/**
 * Processes a single suggestion section by matching it with a response section.
 * Suggestion sections contain directly renderable items from the backend.
 * Only cross-app items are included (native item references are filtered out).
 */
const processSuggestionSection = (
	configSection: SuggestionMenuSection,
	responseSection: ProcessedSuggestionSection | undefined,
): RenderableMenuSectionData | null => {
	if (!responseSection || responseSection.items.length === 0) {
		return null;
	}
	return {
		type: 'suggestionSection',
		heading: configSection.heading,
		foldable: configSection.foldable,
		items: responseSection.items,
	};
};

/**
 * Hydrates app-defined sections by matching native items from the API response with configured sections.
 *
 * This function:
 * 1. Takes the API response data and the original sections configuration
 * 2. For each app-defined section in the configuration:
 *    - Uses section metadata (heading, foldable, etc.) from config
 *    - Uses item ordering from the response
 *    - For native items: finds matching config item by ATI to preserve properties (onClick, children, etc.)
 *    - For cross-app items: uses data directly from response
 * 3. Returns hydrated sections ready for rendering
 *
 * Note: Sections with no items after hydration are filtered out (returns null in array).
 */
const hydrateAppDefinedSections = (
	configSections: AppDefinedMenuSection[],
	data: MenuItemsAPIResponse,
): Array<RenderableMenuSectionData | null> => {
	return getHydrateMenuItems(data, configSections);
};

/**
 * Parameters for computing renderable sections.
 */
export interface ComputeRenderableSectionsParams {
	/** API response data containing hydrated items */
	data: MenuItemsAPIResponse | undefined;
	/** Whether an error occurred fetching data */
	hasError: boolean;
	/** Whether to only render native items (no cross-app hydration) */
	isNativeOnly: boolean;
	/** The menu sections to render */
	sections: MenuSectionV2[];
}

/**
 * Computes renderable sections based on data state and error conditions.
 *
 * When data is available and not native-only mode:
 * - Suggestion sections: Use items directly from API response (already renderable)
 * - App-defined sections: Hydrate items by matching response with config
 *
 * When there's an error, no data, or native-only mode:
 * - Suggestion sections: Cannot be shown (require backend data)
 * - App-defined sections: Show native items only as fallback
 *
 * This function implements defensive programming to handle badly formatted backend responses:
 * - Filters out null/empty sections that may result from hydration failures
 * - Ensures data is rendered as much as possible without causing errors
 * - Maintains section order from original config while handling missing data gracefully
 */
export const computeRenderableSections = ({
	sections,
	data,
	hasError,
	isNativeOnly,
}: ComputeRenderableSectionsParams): RenderableMenuSectionData[] => {
	// Error/no data/native-only case: return only native items from app-defined sections.
	// Also handles edge case where data is returned but without valid sections information
	// (behind feature gate navx-3612_safe_data_access) to avoid runtime errors.
	const hasInvalidSections =
		data && (!data.sections || !Array.isArray(data.sections)) && fg('navx-3612_safe_data_access');

	if (hasError || !data || isNativeOnly || hasInvalidSections) {
		const appDefinedSections = getAppDefinedSections(sections);
		return filterNativeItemsOnly(appDefinedSections);
	}

	// Separate sections by type for processing
	const suggestionResponseSections = data.sections.filter(
		(section): section is ProcessedSuggestionSection => section.type === 'suggestionSection',
	);
	const appDefinedConfigSections = getAppDefinedSections(sections);

	// Hydrate app-defined sections with response data
	// This returns an array that may contain null entries for sections that couldn't be hydrated
	const hydratedAppSections = hydrateAppDefinedSections(appDefinedConfigSections, data);

	// Process sections in original order, matching with response data
	// Use separate indices to track position in filtered arrays
	let suggestionIndex = 0;
	let appSectionIndex = 0;

	return sections
		.map((configSection) => {
			if (isSuggestionSection(configSection)) {
				const responseSection = suggestionResponseSections[suggestionIndex++];
				return processSuggestionSection(configSection, responseSection);
			}
			if (isAppDefinedSection(configSection)) {
				// Get the hydrated section (may be null if hydration failed)
				const hydratedSection = hydratedAppSections[appSectionIndex++];
				return hydratedSection;
			}
			return null;
		})
		.filter((section): section is RenderableMenuSectionData => section !== null);
};

/**
 * Calculates the total number of items across all sections.
 */
export const computeTotalItemCount = (sections: RenderableMenuSectionData[]): number =>
	sections.reduce((total, section) => total + section.items.length, 0);

/**
 * Section metadata containing pre-computed total count and start indices.
 */
export interface SectionMetadata {
	startIndices: number[];
	totalCount: number;
}

/**
 * Computes all section metadata (total count and start indices) in a single pass.
 * Optimized to compute both values in one reduce operation with O(n) complexity.
 *
 * @param sections - The renderable menu sections to analyze
 * @returns Object containing totalCount and array of startIndices for each section
 *
 * @example
 * const metadata = computeSectionMetadata(sections);
 * // Use in render:
 * // totalMenuItemsCount={metadata.totalCount}
 * // sectionStartIndex={metadata.startIndices[sectionIndex]}
 */
export const computeSectionMetadata = (sections: RenderableMenuSectionData[]): SectionMetadata => {
	let totalCount = 0;
	const startIndices: number[] = [];

	sections.forEach((section) => {
		startIndices.push(totalCount);
		totalCount += section.items.length;
	});

	return { totalCount, startIndices };
};
